import React, { useState } from 'react';

import PageLayout from 'layouts/Helpers/PageLayout';

import {
    Button
} from '@material-ui/core';

import { getAllOeuvres, getOeuvreById, updateOeuvre, updateMultiple, getAllContacts, getContactById, updateContact } from 'Api';
import { containsGenre, addGenre, filtreOeuvresActuelles } from 'Util/Contacts';

import { displayOeuvreDerniereLocalisationFull, displayOeuvreDerniereLocalisation } from 'Util/OeuvreLocalisation';
import { displayOeuvreLastProprio } from 'Util/OeuvreProprietaire';
import { calcMultipleDatas } from 'Util/OeuvreMultiple';
import { getSurfaceDiameter, getSurfaceRect } from 'Util/Oeuvre';
import { isOeuvreMultiple } from 'Util/Oeuvre';
import { localisationsWithNumero } from 'Util/OeuvreMultiple';
import { calcLastLocalisation } from 'Util/OeuvreLocalisation';

import { LazyLog, ScrollFollow } from "@melloware/react-logviewer";

function ProgressBar({ progress, progressMax }) {
    const containerStyles = {
        height: '20px',
        width: '100%',
        backgroundColor: '#e0e0de',
        borderRadius: '50px',
        margin: '20px 0',
    };

    const fillerStyles = {
        height: '100%',
        width: `${100 * progress / progressMax}%`,
        backgroundColor: 'blue',
        borderRadius: 'inherit',
        textAlign: 'right',
        transition: 'width 1s ease-in-out',
    };

    const labelStyles = {
        padding: '5px',
        color: 'white',
        fontWeight: 'bold',
    };

    return (
        <div style={containerStyles}>
            <div style={fillerStyles}>
                <span style={labelStyles}>{`${progress} / ${progressMax}`}</span>
            </div>
        </div>
    );
}

const Layout = ({ initialData, reloadData = () => {}, onSaved = async () => {}, saveData = async () => {}, layout = 'edit' }) => {

    const [progress, setProgress] = useState(0);
    const [progressMax, setProgressMax] = useState(0);
    const [logText, setLogText] = useState("");

    const fileName = "log.txt";

    const addLogInfo = (newInfo) => {
        setLogText((prevInfoString) => `${prevInfoString}\n${newInfo}`);
    };

    const exportToFile = () => {
        const blob = new Blob([logText], { type: 'text/plain' });
        const fileUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(fileUrl);
    };

    const processOeuvres = async () => {
        try {
            const artworks = await getAllOeuvres();
            const oeuvres = artworks.oeuvres.filter(o => !isOeuvreMultiple(o)).sort((a, b) => a.archive - b.archive);
            setProgress(0);
            setProgressMax(oeuvres.length);

            for (let i = 0; i < oeuvres.length; i++) {
                await processOneOeuvre(oeuvres[i]);
                setProgress(i + 1);
            }
        } catch (err) {
            addLogInfo(`Erreur dans processOeuvres : ${err.message}`);
        }
    };

    const processOneOeuvre = async (oeuvre) => {
        try {
            const id = oeuvre.objectid;
            const oeuvreFetch = await getOeuvreById({ id });
            const data = oeuvreFetch.oeuvre;

            addLogInfo("oeuvre : " + data.archive);

            data.dernier_proprietaire = displayOeuvreLastProprio(data);
            data.derniere_localisation_full = displayOeuvreDerniereLocalisationFull(data);
            data.derniere_localisation = displayOeuvreDerniereLocalisation(data);
            data.surface = data.diametre ? getSurfaceDiameter(data) : getSurfaceRect(data);
            await updateOeuvre({ id: data.objectid, data });
        } catch (err) {
            addLogInfo(`Erreur dans processOneOeuvre : ${err.message}`);
        }
    };

    const processMultiples = async () => {
        try {
            const artworks = await getAllOeuvres();
            const multiples = artworks.oeuvres.filter(o => isOeuvreMultiple(o)).sort((a, b) => a.archive - b.archive);
            setProgress(0);
            setProgressMax(multiples.length);

            for (let i = 0; i < multiples.length; i++) {
                await processOneMultiple(multiples[i]);
                setProgress(i + 1);
            }
        } catch (err) {
            addLogInfo(`Erreur dans processMultiples : ${err.message}`);
        }
    };

    const processOneMultiple = async (oeuvre) => {
        try {
            const id = oeuvre.objectid;
            const oeuvreFetch = await getOeuvreById({ id });
            const data = oeuvreFetch.oeuvre;
    
            if (isOeuvreMultiple(data)) {
                calcMultipleDatas(data);
                for (const multiple of data.multiples) {
                    try {
                        multiple.oeuvre_id = data.objectid;
                        await updateMultiple({ oeuvreid: multiple.oeuvre_id, data: multiple });
                        addLogInfo("oeuvre : " + data.archive + " multiple : " + multiple.numero);
                    } catch (innerErr) {
                        addLogInfo(`Erreur sur updateMultiple : ${innerErr.message}`);
                        addLogInfo(`oeuvre : ${JSON.stringify(data)}`);
                        addLogInfo(`multiple : ${JSON.stringify(multiple)}`);
                    }
                }
            }
        } catch (err) {
            addLogInfo(`Erreur dans processOneMultiple : ${err.message}`);
            addLogInfo(`oeuvre : ${JSON.stringify(oeuvre)}`);
        }
    };
    

    const checkEmplacements = async () => {
        try {
            const artworks = await getAllOeuvres();
            const multiples = artworks.oeuvres.filter(o => isOeuvreMultiple(o)).sort((a, b) => a.archive - b.archive);
            setProgress(0);
            setProgressMax(multiples.length);

            for (let i = 0; i < multiples.length; i++) {
                await checkOneEmplacement(multiples[i]);
                setProgress(i + 1);
            }
        } catch (err) {
            addLogInfo(`Erreur dans checkEmplacements : ${err.message}`);
        }
    };

    const checkOneEmplacement = async (oeuvre) => {
        try {
            const id = oeuvre.objectid;
            const oeuvreFetch = await getOeuvreById({ id });
            const data = oeuvreFetch.oeuvre;

            if (isOeuvreMultiple(data)) {
                for (const multiple of data.multiples) {
                    const emplacement = multiple.emplacement || "";
                    const localisations = localisationsWithNumero(data.localisations, multiple.numero);
                    const lastLocalisation = calcLastLocalisation(localisations);
                    const dernier_emplacement = lastLocalisation?.emplacement || "";

                    if (emplacement !== dernier_emplacement) {
                        addLogInfo(`oeuvre : ${data.archive} multiple : ${multiple.numero} emplacement: ${emplacement} lastEmplacement : ${dernier_emplacement}`);
                    }
                }
            }
        } catch (err) {
            addLogInfo(`Erreur dans checkOneEmplacement : ${err.message}`);
        }
    };

    const checkCollectionneurs = async () => {
        try {
            const alllContacts = await getAllContacts();
            const contacts = alllContacts.contacts;

            setProgress(0);
            setProgressMax(contacts.length);

            for (let i = 0; i < contacts.length; i++) {
                const contactById = await getContactById({ id: contacts[i].objectid });
                const contact = contactById.contact;
                const oeuvres = contact.oeuvres_contact;

              //  const isCollectionneur = containsGenre(contact, "collectionneur");
                addLogInfo(`contact : ${contact.nom} - ${contact.structure}`);
                for (const oeuvre of oeuvres) {
                    addLogInfo(`oeuvre : ${oeuvre.archive} - ${oeuvre.titre}`);
                }
                setProgress(i + 1);
            }
        } catch (err) {
            addLogInfo(`Erreur dans checkCollectionneurs : ${err.message}`);
        }
    };

    const addCollectionneurs = async () => {
        try {
            const alllContacts = await getAllContacts();
            const contacts = alllContacts.contacts;

            setProgress(0);
            setProgressMax(contacts.length);

            for (let i = 0; i < contacts.length; i++) {
                const contactId = contacts[i].objectid;
                const contactById = await getContactById({ id: contactId });
                const contact = contactById.contact;
                const oeuvres = contact.oeuvres_contact;
                const isCollectionneur = containsGenre(contact, "collectionneur");

                addLogInfo(`contact : ${contact.nom} - ${contact.structure}`);

                if (!isCollectionneur) {
                    const oeuvresActuelles = filtreOeuvresActuelles(oeuvres, contactId);
                    if (oeuvresActuelles?.length > 0) {
                        addGenre(contact, "collectionneur");
                        await updateContact({ id: contactId, data: contact });
                        addLogInfo("add tag collectionneur");
                    }
                } else {
                    addLogInfo("deja collectionneur");
                }

                setProgress(i + 1);
            }
        } catch (err) {
            addLogInfo(`Erreur dans addCollectionneurs : ${err.message}`);
        }
    };

    return (
        <PageLayout title="Maintenance">
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div>
                    <Button style={{ marginRight: '20px' }} onClick={processOeuvres} variant="contained">Recalc Oeuvres</Button>
                    <Button style={{ marginRight: '20px' }} onClick={processMultiples} variant="contained">Recalc Multiples</Button>
                    <Button style={{ marginRight: '20px' }} onClick={checkEmplacements} variant="contained">Check emplacement</Button>
                    <Button style={{ marginRight: '20px' }} onClick={checkCollectionneurs} variant="contained">Liste Collectionneurs</Button>
                    <Button style={{ marginRight: '20px' }} onClick={addCollectionneurs} variant="contained">Add Collectionneurs</Button>
                </div>
                <div>
                    <ProgressBar progress={progress} progressMax={progressMax} />
                    <Button style={{ marginRight: '20px' }} variant="contained" onClick={exportToFile}>Exporter le fichier texte</Button>
                </div>
                <div style={{ height: '500px' }}>
                    <ScrollFollow startFollowing={true} render={({ follow, onScroll }) => (
                        <LazyLog text={logText} selectableLines width="200" stream follow={follow} onScroll={onScroll} />
                    )} />
                </div>
            </div>
        </PageLayout>
    );
};

export default ({ initialData, ...props }) => {
    const key = initialData ? initialData.objectid : "create";
    return <Layout {...props} initialData={initialData} key={key} />;
};
